// Shared building blocks for the non-board pages (plain DOM, no framework).
import type { MeResponse, WorkspaceInfo } from '../../shared/api.ts';
import { get, patch, post } from '../api.ts';
import { h } from '../util/dom.ts';
import { logoMark } from './logo.ts';

export const PRODUCT = 'Atomic Lab';

export function mount(...nodes: Node[]): void {
  document.getElementById('app')!.replaceChildren(...nodes);
}

export function setTitle(title?: string): void {
  document.title = title ? `${title} · ${PRODUCT}` : PRODUCT;
}

export function centered(...children: Node[]): HTMLElement {
  const brand = h('a', { class: 'card-brand', href: '/' }, [logoMark(28), PRODUCT]);
  return h('main', { class: 'center' }, [h('div', { class: 'card' }, [brand, ...children])]);
}

export function loading(): void {
  mount(h('main', { class: 'center muted', 'aria-busy': 'true' }, ['Loading…']));
}

export function errorPage(err: unknown): void {
  const notFound = (err as { status?: number } | null)?.status === 404;
  setTitle(notFound ? 'Not found' : 'Error');
  mount(centered(
    h('h1', {}, [notFound ? 'Not found' : 'Something went wrong']),
    h('p', { class: 'muted' }, [notFound
      ? "This page doesn't exist, or you don't have access to it."
      : err instanceof Error ? err.message : String(err)]),
    h('a', { class: 'btn', href: '/' }, ['Go to your boards']),
  ));
}

/** A labelled form control. */
export function field(label: string, control: HTMLElement, hint?: string): HTMLElement {
  return h('label', { class: 'field' }, [
    h('span', { class: 'field-label' }, [label]),
    control,
    ...(hint ? [h('span', { class: 'field-hint' }, [hint])] : []),
  ]);
}

export function formError(): HTMLParagraphElement & { show(err: unknown): void; clear(): void } {
  const p = h('p', { class: 'form-error', role: 'alert', hidden: '' }) as HTMLParagraphElement & { show(err: unknown): void; clear(): void };
  p.show = (err) => {
    p.textContent = err instanceof Error ? err.message : String(err);
    p.hidden = false;
  };
  p.clear = () => {
    p.textContent = '';
    p.hidden = true;
  };
  return p;
}

/** Disables a submit button while `work` runs. */
export async function busy<T>(button: HTMLButtonElement, work: () => Promise<T>): Promise<T> {
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    return await work();
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

export function select(options: [value: string, label: string][], value: string, attrs: Record<string, string> = {}): HTMLSelectElement {
  const s = h('select', attrs, options.map(([v, label]) => h('option', { value: v }, [label])));
  s.value = value;
  return s;
}

const LAST_WS_KEY = 'last-workspace';
export function rememberWorkspace(slug: string): void {
  try { localStorage.setItem(LAST_WS_KEY, slug); } catch { /* ignore */ }
}
export function lastWorkspace(): string | null {
  try { return localStorage.getItem(LAST_WS_KEY); } catch { return null; }
}

/**
 * The signed-in user. A new account has no name yet: ask for it first (which
 * also creates their workspace), then carry on to the page they were opening.
 */
export async function getMe(): Promise<MeResponse> {
  const me = await get<MeResponse>('/api/me');
  if (me.user.name) return me;
  welcomePage();
  return new Promise<never>(() => {}); // the welcome form reloads the page
}

export function welcomePage(): void {
  setTitle('Welcome');
  const name = h('input', { type: 'text', required: '', maxlength: '80', autocomplete: 'name', placeholder: 'e.g. Sam Taylor' });
  const submit = h('button', { type: 'submit', class: 'btn primary' }, ['Continue']);
  const error = formError();
  const form = h('form', { class: 'stack' }, [field('Your name', name, 'Shown to people you work with.'), error, submit]);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    error.clear();
    void busy(submit, async () => {
      try {
        await patch('/api/me', { name: name.value });
        location.reload();
      } catch (err) {
        error.show(err);
      }
    });
  });
  mount(centered(h('h1', {}, [`Welcome to ${PRODUCT}`]), h('p', { class: 'muted' }, ['One last thing: what should we call you?']), form));
  name.focus();
}

export function header(me: MeResponse, current?: WorkspaceInfo, active?: 'boards' | 'members' | 'plan' | 'settings'): HTMLElement {
  const nav: Node[] = [];
  if (current) {
    const switcher = select(
      [...me.workspaces.map((w): [string, string] => [w.slug, w.name]), ['__new', 'New workspace…']],
      current.slug, { class: 'ws-switcher', 'aria-label': 'Workspace' });
    switcher.addEventListener('change', () => {
      location.assign(switcher.value === '__new' ? '/new-workspace' : `/w/${switcher.value}`);
    });
    nav.push(switcher);
    const link = (href: string, label: string, on: boolean) =>
      h('a', { href, class: on ? 'nav-link active' : 'nav-link', ...(on ? { 'aria-current': 'page' } : {}) }, [label]);
    nav.push(link(`/w/${current.slug}`, 'Boards', active === 'boards'));
    if (current.role !== 'guest') nav.push(link(`/w/${current.slug}/members`, 'Members', active === 'members'));
    if (current.role !== 'guest') nav.push(link(`/w/${current.slug}/plan`, 'Plan', active === 'plan'));
    if (current.role === 'owner' || current.role === 'admin') nav.push(link(`/w/${current.slug}/settings`, 'Settings', active === 'settings'));
  }
  const signOut = h('button', { type: 'button', class: 'link-btn' }, ['Sign out']);
  signOut.addEventListener('click', async () => {
    await post('/api/auth/logout').catch(() => {});
    location.assign('/');
  });
  return h('header', { class: 'app-header' }, [
    h('a', { class: 'brand', href: '/', 'aria-label': `${PRODUCT} home` }, [logoMark(24), PRODUCT]),
    ...nav,
    h('div', { class: 'spacer' }),
    h('a', { class: 'user-name', href: '/account', title: `${me.user.email}: your account` }, [me.user.name]),
    signOut,
  ]);
}

/** Under buttons that can create an account (spec §16.1). */
export function termsNote(): HTMLElement {
  return h('p', { class: 'signup-terms' }, [
    'By continuing, you agree to the ', h('a', { href: '/terms' }, ['Terms']),
    ' and acknowledge the ', h('a', { href: '/privacy' }, ['Privacy policy']), '.',
  ]);
}

/** Links to the privacy policy and terms (spec §16.1), after any `first`. */
export function legalLinks(first: Node[] = []): HTMLElement {
  return h('footer', { class: 'legal-links' }, [
    ...first, h('a', { href: '/privacy' }, ['Privacy']), h('a', { href: '/terms' }, ['Terms']),
  ]);
}

/** A strip at the top of a page: information, or (`warn`) something that needs doing. */
export function notice(content: (Node | string)[], actions: Node[] = [], tone: 'info' | 'warn' = 'info'): HTMLElement {
  return h('section', { class: `notice ${tone}`, role: tone === 'warn' ? 'alert' : 'status' }, [
    h('p', {}, content),
    ...(actions.length ? [h('div', { class: 'notice-actions' }, actions)] : []),
  ]);
}

export const longDate = (ts: number) => new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

/** Whole days until `ts` (at least 1 while it's still ahead). */
export const daysLeft = (ts: number, now = Date.now()) => Math.max(ts > now ? 1 : 0, Math.ceil((ts - now) / 86_400_000));

export function relativeTime(ts: number, now = Date.now()): string {
  const s = Math.round((now - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const hrs = Math.round(m / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const d = Math.round(hrs / 24);
  if (d < 30) return `${d} d ago`;
  return new Date(ts).toLocaleDateString();
}

export const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', member: 'Member', guest: 'Guest', editor: 'Can edit', viewer: 'Can view',
};
